import React, { useState, useEffect } from 'react';
import { Upload, Delete } from 'baseui/icon';
import { Button, SHAPE } from 'baseui/button';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _forEach from 'lodash/forEach';
import _union from 'lodash/union';
import _keys from 'lodash/keys';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _values from 'lodash/values';

import { EMPTY_STRING, EMPTY_NUMBER } from 'commons-ui';
import apkIcon from '../../../../assets/misc/apk.png';

import useStyles from './useStyles';
import useService from './useService';
import { ASSET_TYPE, useAssetUploadOnS3Props, AssetUploadOnS3Props, ACCEPTED_FILE_TYPES } from './constants';

const useAssetUploadOnS3 = ({
	value,
	onChange,
	setShowLoader,
	assetType,
	multiple = false,
	maxFileSizeMB,
	subFolder,
	zupeeUserId,
	usePresignedUrl
}: useAssetUploadOnS3Props) => {
	const { uploadToS3, uploadMultipleToS3 } = useService();
	const [uploadedAssetUrl, setUploadedAssetUrl] = useState<any>();

	useEffect(() => {
		if (!_isNil(uploadedAssetUrl) && !_isEmpty(uploadedAssetUrl)) {
			if (multiple) {
				onChange(_filter(_union(value || [], _keys(uploadedAssetUrl)), (url) => !uploadedAssetUrl?.[url]?.isRemoved));
			} else {
				const { isRemoved } = _values(uploadedAssetUrl)[0];
				onChange(isRemoved ? undefined : _keys(uploadedAssetUrl)[0]);
			}
		}
	}, [uploadedAssetUrl]);

	const handleFileUpload = async (event: React.InputHTMLAttributes<HTMLInputElement>) => {
		setShowLoader(true);
		if (multiple) {
			const assetUrls = await uploadMultipleToS3(_get(event, ['target', 'files']), assetType);
			setShowLoader(false);
			const newUploadedAssetUrls = { ...uploadedAssetUrl };
			_forEach(assetUrls, (assetUrl) => {
				newUploadedAssetUrls[assetUrl?.assetUrl] = { fileName: assetUrl?.fileName, isRemoved: false };
			});
			setUploadedAssetUrl(newUploadedAssetUrls);
		} else {
			const assetUrl = await uploadToS3(
				_get(event, ['target', 'files', 0]),
				assetType,
				maxFileSizeMB,
				subFolder,
				zupeeUserId,
				usePresignedUrl
			);
			setShowLoader(false);
			if (assetUrl) setUploadedAssetUrl({ [assetUrl?.assetUrl]: { fileName: assetUrl?.fileName, isRemoved: false } });
		}
	};

	const handleRemoveFile = (assetUrlToRemove: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setUploadedAssetUrl({
			...uploadedAssetUrl,
			[assetUrlToRemove]: { fileName: uploadedAssetUrl?.[assetUrlToRemove]?.fileName, isRemoved: true }
		});
		event.preventDefault();
	};

	const getImageSource = (fileToShow: string) => {
		const allowedMimeTypes: Set<string> = new Set([
			ASSET_TYPE.IMAGE,
			ASSET_TYPE.MEME,
			ASSET_TYPE.THUMBNAIL,
			ASSET_TYPE.MEDIA_PUBLIC,
			ASSET_TYPE.MEDIA_PRIVATE,
			ASSET_TYPE.LAUNCH_VIDEO,
			ASSET_TYPE.MISC_ASSET
		]);
		if (assetType === ASSET_TYPE.APK && fileToShow && fileToShow.length > 1) {
			return apkIcon;
		}
		if (assetType === ASSET_TYPE.SPOTLIGHT_VIDEO) {
			return fileToShow;
		}
		if (allowedMimeTypes.has(assetType)) {
			return fileToShow;
		}
		return '';
	};

	return { handleFileUpload, handleRemoveFile, getImageSource, uploadedAssetUrl };
};

/**
 * Asset Upload Component that uploads the asset to s3 and returns the s3 path.
 */
const AssetUploadOnS3: React.FC<AssetUploadOnS3Props> = ({
	value,
	assetType,
	disabled,
	onChange,
	setShowLoader,
	multiple = false,
	downloadFile = false,
	displayText = EMPTY_STRING,
	link = EMPTY_STRING,
	maxFileSizeMB = EMPTY_NUMBER,
	subFolder,
	zupeeUserId,
	usePresignedUrl
}) => {
	const { handleFileUpload, handleRemoveFile, getImageSource, uploadedAssetUrl } = useAssetUploadOnS3({
		value,
		assetType,
		onChange,
		setShowLoader,
		multiple,
		maxFileSizeMB,
		subFolder,
		zupeeUserId,
		usePresignedUrl
	});
	const styles = useStyles();

	const iconStyleProps = { className: disabled ? styles.disabledStyle : styles.iconActiveStyle };

	const renderFile = (fileToShow: string) => {
		let allowedMimeTypes: Set<string> = new Set([ASSET_TYPE.SPOTLIGHT_VIDEO, ASSET_TYPE.LAUNCH_VIDEO]);
		if (allowedMimeTypes.has(assetType)) {
			return (
				<div className={styles.fileContainerStyle}>
					{allowedMimeTypes.has(assetType) ? (
						<video src={getImageSource(fileToShow)} className={styles.imageStyle} controls />
					) : (
						_get(uploadedAssetUrl, [fileToShow, 'fileName'], fileToShow)
					)}
					<Button shape={SHAPE.circle} size='mini' onClick={handleRemoveFile(fileToShow)} {...iconStyleProps}>
						<Delete size={20} {...iconStyleProps} />
					</Button>
				</div>
			);
		}
		allowedMimeTypes = new Set([ASSET_TYPE.IMAGE, ASSET_TYPE.MEME, ASSET_TYPE.THUMBNAIL, ASSET_TYPE.MISC_ASSET]);
		return (
			<div className={styles.fileContainerStyle}>
				<div>
					{allowedMimeTypes.has(assetType) ? (
						<img src={getImageSource(fileToShow)} className={styles.imageStyle} alt='' />
					) : (
						_get(uploadedAssetUrl, [fileToShow, 'fileName'], fileToShow)
					)}
				</div>
				<Button shape={SHAPE.circle} size='mini' onClick={handleRemoveFile(fileToShow)} {...iconStyleProps}>
					<Delete size={20} {...iconStyleProps} />
				</Button>
			</div>
		);
	};
	const renderValue = () => {
		if (multiple) {
			return (
				<>
					{_map(value, (fileToShow) => (
						<>{renderFile(fileToShow)}</>
					))}
				</>
			);
		}
		return renderFile(value);
	};

	const id = `asset-input-${assetType}-${Math.floor(Math.random() * 10000000)}`;
	return (
		<>
			<div className={disabled ? [styles.containerStyle, styles.disabledStyle].join(' ') : styles.containerStyle}>
				<label htmlFor={id}>
					<Upload size={40} />
				</label>
				<input
					id={id}
					type='file'
					className={styles.inputStyle}
					accept={ACCEPTED_FILE_TYPES[assetType]}
					onChange={handleFileUpload}
					disabled={disabled}
					multiple={multiple}
				/>
				{(!multiple && value) || (multiple && value && value.length > 0) ? (
					<div className={styles.imageContainerStyle}>{renderValue()}</div>
				) : null}
			</div>
			{downloadFile ? (
				<a href={link} className={styles.downloadFileContainerStyle}>
					{displayText}
				</a>
			) : null}
		</>
	);
};

export default AssetUploadOnS3;
